import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import Seo from "../lib/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { fetchCenters } from "../lib/api";
import { REGIONS } from "../lib/regions";

export default function Regiones() {
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            const results = {};
            await Promise.all(
                REGIONS.map(async (region) => {
                    try {
                        const data = await fetchCenters({ region: region.name, page: 1, limit: 1 });
                        results[region.name] = data.total || 0;
                    } catch {
                        results[region.name] = 0;
                    }
                }),
            );
            if (mounted) {
                setCounts(results);
                setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    return (
        <>
            <Seo
                title="Centros de rehabilitación de drogas por región de Chile"
                description="Encuentra centros de rehabilitación de drogas en cada una de las 16 regiones de Chile: Metropolitana, Valparaíso, Biobío, Araucanía y más."
                path="/regiones"
                keywords="centros de rehabilitación por región, rehabilitación Santiago, rehabilitación Valparaíso, rehabilitación Biobío"
            />

            <section className="border-b border-slate-800 bg-slate-950">
                <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
                    <Breadcrumbs
                        items={[
                            { name: "Inicio", path: "/" },
                            { name: "Regiones" },
                        ]}
                    />
                    <h1 className="mt-5 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                        Centros de rehabilitación de drogas por región
                    </h1>
                    <p className="mt-3 max-w-2xl text-slate-400">
                        Chile cuenta con 16 regiones y una red creciente de centros
                        acreditados. Elige tu región para ver los centros disponibles.
                    </p>
                </div>
            </section>

            <section className="py-12">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div
                        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                        data-testid="regiones-grid"
                    >
                        {REGIONS.map((region) => {
                            const count = counts[region.name] || 0;
                            return (
                                <Link
                                    key={region.slug}
                                    to={`/regiones/${region.slug}`}
                                    data-testid={`region-card-${region.slug}`}
                                    className="card-hover group rounded-xl border border-slate-800 bg-slate-900 p-5"
                                >
                                    <div className="flex items-center gap-2 text-cyan-400">
                                        <MapPin className="h-4 w-4" />
                                        <span className="text-xs font-medium uppercase tracking-wider">
                                            Región
                                        </span>
                                    </div>
                                    <h2 className="mt-3 text-lg font-semibold text-slate-50 group-hover:text-cyan-400">
                                        {region.shortName}
                                    </h2>
                                    <p className="mt-2 text-sm text-slate-400">
                                        {loading
                                            ? "Cargando centros..."
                                            : count > 0
                                              ? `${count} centro${count === 1 ? "" : "s"} disponible${count === 1 ? "" : "s"}`
                                              : "Consulta disponibilidad"}
                                    </p>
                                    <p className="mt-1 text-xs text-slate-500">
                                        Capital: {region.capital}
                                    </p>
                                    <div className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-cyan-400">
                                        Ver región{" "}
                                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>
        </>
    );
}
